import { useRef } from 'react';
import type { CSSProperties } from 'react';

import { cn } from 'helpers/classnames';
import type { ImageProps } from 'components/common/Image';
import Image from 'components/common/Image';
import { useImageRotation } from 'hooks/useImageRotation';
import usePrevious from 'hooks/usePrevious';
import { CrossfadeTransition } from 'components/common/CrossfadeTransition';

/* eslint-disable css-modules/no-unused-class */
import css from 'styles/components/common/card/cardMedia.scss';

export const FADE_MS = 300;

export interface CardMediaMultiImagePreviewProps {
  isDesktop: boolean;
  isHovered: boolean;
  prefersReducedMotion?: boolean;
  mobileImageIndex?: number;
  allImages: ImageProps[];
  mainImage: ImageProps;
  imageClassName?: string;
  fullSizeImage?: boolean;
  styleId: string | number;
  imageLoadingStrategy?: 'eager' | 'lazy';
}

/**
 * The CardMedia variant for multi-image-preview-eligible cards. On desktop it auto-rotates
 * the product's angles while hovered; on touch devices it follows `mobileImageIndex` from
 * the swipe cursor. Snaps back to MAIN with no fade on a swatch swap, and crossfades between
 * angles via CrossfadeTransition.
 * Shopbop reference: https://code.amazon.com/packages/ShopbopSharedReactComponents/blobs/mainline/--/packages/shared-react-components/src/Molecules/ProductTile/index.tsx
 */
export const CardMediaMultiImagePreview = ({
  isDesktop,
  isHovered,
  prefersReducedMotion = false,
  mobileImageIndex = 0,
  allImages,
  mainImage,
  styleId,
  imageLoadingStrategy = 'lazy',
  imageClassName,
  fullSizeImage
}: CardMediaMultiImagePreviewProps) => {
  const containerRef = useRef<HTMLElement>(null);

  // Desktop auto-rotates while hovered; mobile is driven by the swipe cursor.
  const hoverIndex = useImageRotation({ images: allImages, isActive: isDesktop && isHovered && !prefersReducedMotion });
  const targetIndex = isDesktop ? hoverIndex : mobileImageIndex;

  // A swatch swap rebuilds `allImages`; snap straight to MAIN with no fade.
  const previousStyleId = usePrevious(styleId);
  const hasStyleIdChanged = previousStyleId !== styleId;

  const activeIndex = hasStyleIdChanged ? 0 : Math.min(Math.max(targetIndex, 0), allImages.length - 1);
  const activeImage = activeIndex <= 0 ? mainImage : (allImages[activeIndex] ?? mainImage);

  // Feed the JS fade duration to CSS so the timing has a single source of truth.
  const fadeStyle = { '--card-fade-ms': `${FADE_MS}ms` } as CSSProperties;

  return (
    <CrossfadeTransition
      item={activeImage}
      getKey={img => img.src ?? ''}
      durationMs={FADE_MS}
      skipFade={hasStyleIdChanged}
      className={css.crossfade}
      style={fadeStyle}
      containerRef={containerRef}
    >
      {(img, phase) => (
        <Image
          className={cn(css.slide, phase === 'exiting' ? css.slideHidden : css.slideVisible, { [css.fullSizeImage]: fullSizeImage }, imageClassName)}
          {...img}
          fetchPriority="auto"
          decoding="sync"
          loading={img.src === mainImage.src ? imageLoadingStrategy : 'lazy'}
        />
      )}
    </CrossfadeTransition>
  );
};
